/** Order, payment, invoice and list-filter payload schemas. */

import { z } from "zod";
import { INVOICE_PAGE_SIZE_IDS, INVOICE_ASSET_FORMATS } from "../constants/invoice";
import { ORDER_DEFAULTS, PAYMENT_METHODS } from "../constants/orders";
import { AUDIT_DEFAULTS } from "../constants/audit";
import { LOCALES } from "../constants/config";
import {
  emailSchema,
  moneySchema,
  optionalIsoDateTimeSchema,
  pageQuerySchema,
  phoneSchema,
  positiveMoneySchema,
  uuidSchema,
} from "./common";

const paymentMethodValues = [
  PAYMENT_METHODS.cash,
  PAYMENT_METHODS.transfer,
  PAYMENT_METHODS.qris,
  PAYMENT_METHODS.other,
] as const;

const orderStatusValues = [
  "draft",
  "unpaid",
  "partially_paid",
  "paid",
  "cancelled",
  "completed",
] as const;

const invoiceKindValues = ["full", "down_payment", "balance", "adjustment"] as const;

export const settlementTypeSchema = z.enum(["full", "down_payment"]);

export const orderLineSchema = z.object({
  product_id: uuidSchema,
  variant_id: uuidSchema.nullish(),
  quantity: z.coerce
    .number()
    .int()
    .min(ORDER_DEFAULTS.minLineQuantity)
    .max(ORDER_DEFAULTS.maxLineQuantity),
});

export const createOrderSchema = z
  .object({
    customer_name: z.string().trim().min(2).max(120),
    customer_phone: phoneSchema.nullish(),
    customer_email: emailSchema.nullish(),
    settlement_type: settlementTypeSchema,
    paid_amount: moneySchema.default(0),
    notes: z.string().trim().max(500).nullish(),
    due_at: optionalIsoDateTimeSchema,
    items: z.array(orderLineSchema).min(1).max(ORDER_DEFAULTS.maxLines),
  })
  .refine(
    (value) => value.settlement_type !== "down_payment" || value.paid_amount > 0,
    { message: "A down payment must be greater than zero.", path: ["paid_amount"] },
  );

export const settlePaymentSchema = z.object({
  amount: positiveMoneySchema,
  method: z.enum(paymentMethodValues).default(PAYMENT_METHODS.cash),
  reference: z.string().trim().max(120).nullish(),
  notes: z.string().trim().max(500).nullish(),
});

export const cancelOrderSchema = z.object({
  reason: z.string().trim().max(300).optional(),
});

export const returnOrderLineSchema = z.object({
  order_item_id: uuidSchema,
  quantity: z.coerce.number().int().min(0).max(ORDER_DEFAULTS.maxLineQuantity).default(0),
  damaged_quantity: z.coerce.number().int().min(0).max(ORDER_DEFAULTS.maxLineQuantity).default(0),
});

export const returnOrderSchema = z
  .object({
    note: z.string().trim().max(300).nullish(),
    lines: z.array(returnOrderLineSchema).min(1).max(ORDER_DEFAULTS.maxLines),
  })
  .refine((value) => value.lines.some((line) => line.quantity + line.damaged_quantity > 0), {
    message: "Select at least one unit to return.",
    path: ["lines"],
  });

export const renderInvoiceAssetSchema = z.object({
  page_size: z.enum(INVOICE_PAGE_SIZE_IDS),
  format: z.enum(Object.keys(INVOICE_ASSET_FORMATS) as [string, ...string[]]).default("png"),
  asset_key: z.string().trim().min(1).max(512),
  mime: z.string().trim().min(3).max(64),
  bytes: z.coerce.number().int().positive(),
});

export const voidInvoiceSchema = z.object({
  reason: z.string().trim().max(300).optional(),
});

export const shareInvoiceSchema = z.object({
  channel: z.enum(["whatsapp", "email"]),
  locale: z.enum([LOCALES.en, LOCALES.id]).default(LOCALES.id),
});

export const orderListQuerySchema = pageQuerySchema.extend({
  status: z.enum(orderStatusValues).optional(),
  q: z.string().trim().max(120).optional(),
  from: z.string().trim().optional(),
  to: z.string().trim().optional(),
});

export const invoiceListQuerySchema = pageQuerySchema.extend({
  kind: z.enum(invoiceKindValues).optional(),
  order_id: uuidSchema.optional(),
});

export const logListQuerySchema = pageQuerySchema.extend({
  action: z.string().trim().max(64).optional(),
  resource: z.string().trim().max(64).optional(),
  actor_id: uuidSchema.optional(),
});

export const analyticsQuerySchema = z.object({
  from: z.string().trim().optional(),
  to: z.string().trim().optional(),
  bucket: z.enum(["day", "week", "month"]).default("day"),
  limit: z.coerce.number().int().min(1).max(AUDIT_DEFAULTS.recentLimit * 10).default(5),
});
